import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Typography, 
    Paper, 
    Button,
    Card,
    CardContent,
    Grid,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    Chip,
    Icon,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Switch,
    FormControlLabel,
    Divider
} from '@material-ui/core';
import * as colors from '@material-ui/core/colors';
import moment from 'moment';
import * as Actions from '../../store/actions';
import IOPSStressDialog from '../dialogs/IOPSStressDialog';
import MemoryBandwidthDialog from '../dialogs/MemoryBandwidthDialog';
import GPUStressDialog from '../dialogs/GPUStressDialog';

const StressTestPanel = ({ clusterId, nodes = [], className }) => {
    const dispatch = useDispatch();
    
    // Ensure nodes is an array before mapping - check if nodes.data exists
    const validNodes = (nodes && Array.isArray(nodes.data)) ? nodes.data : (Array.isArray(nodes) ? nodes : []);
       // Local state for stress test configuration
    const [selectedTest, setSelectedTest] = useState('');
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [testDuration, setTestDuration] = useState(60);
    const [testIntensity, setTestIntensity] = useState('medium');
    const [isRunning, setIsRunning] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [showResultsDialog, setShowResultsDialog] = useState(false);
    const [showIOPSDialog, setShowIOPSDialog] = useState(false);
    const [showMemoryBandwidthDialog, setShowMemoryBandwidthDialog] = useState(false);
    const [showGPUDialog, setShowGPUDialog] = useState(false);
    
    // GPU stress test specific options
    const [gpuMemoryTest, setGpuMemoryTest] = useState(true);
    const [gpuComputeTest, setGpuComputeTest] = useState(true);
    
    // IOPS stress test specific options
    const [ioBlockSize, setIoBlockSize] = useState('4k');
    const [ioPattern, setIoPattern] = useState('random');
    const [ioReadWriteRatio, setIoReadWriteRatio] = useState(70); // 70% read, 30% write

    const stressTestTypes = [
        {
            id: 'gpu_stress',
            name: 'GPU Stress Test',
            description: 'Test GPU compute performance, memory bandwidth, and thermal stability',
            icon: 'memory',
            color: colors.orange[500],
            duration: [30, 60, 120, 300], // seconds
            estimatedTime: '1-5 minutes'
        },
        {
            id: 'iops_stress',
            name: 'IOPS Stress Test',
            description: 'Test storage I/O performance with volume selection and detailed metrics',
            icon: 'storage',
            color: colors.brown[500],
            duration: [60, 120, 300, 600], // seconds
            estimatedTime: '1-10 minutes'
        },
        {
            id: 'memory_bandwidth',
            name: 'Memory Bandwidth Test',
            description: 'Test system memory bandwidth and latency under load',
            icon: 'memory',
            color: colors.purple[500],
            duration: [30, 60, 120], // seconds
            estimatedTime: '30 seconds - 2 minutes'
        },
        {
            id: 'network_bandwidth',
            name: 'Network Bandwidth Test',
            description: 'Test inter-node network bandwidth and latency',
            icon: 'network_check',
            color: colors.cyan[500],
            duration: [30, 60, 120], // seconds
            estimatedTime: '30 seconds - 2 minutes'
        }
    ];

    const intensityLevels = [
        { value: 'light', label: 'Light (25%)', description: 'Low impact, safe for production' },
        { value: 'medium', label: 'Medium (50%)', description: 'Moderate load, recommended for testing' },
        { value: 'heavy', label: 'Heavy (75%)', description: 'High load, use with caution' },
        { value: 'maximum', label: 'Maximum (100%)', description: 'Maximum stress, may affect system stability' }
    ];

    const ioBlockSizes = ['512b', '1k', '4k', '8k', '16k', '32k', '64k', '128k', '1m'];
    const ioPatterns = [
        { value: 'random', label: 'Random', description: 'Random read/write pattern' },
        { value: 'sequential', label: 'Sequential', description: 'Sequential read/write pattern' },
        { value: 'mixed', label: 'Mixed', description: 'Mixed random and sequential' }
    ];

    const getSelectedTestConfig = () => {
        return stressTestTypes.find(test => test.id === selectedTest);
    };

    const getNodeSelectionText = () => {
        if (selectedNodes.length === 0) return 'No nodes selected';
        if (selectedNodes.length === 1) return `1 node selected`;
        if (selectedNodes.length === validNodes.length) return 'All nodes selected';
        return `${selectedNodes.length} nodes selected`;
    };

    const handleNodeToggle = (nodeName) => {
        setSelectedNodes(prev => {
            if (prev.includes(nodeName)) {
                return prev.filter(n => n !== nodeName);
            } else {
                return [...prev, nodeName];
            }
        });
    };

    const handleSelectAllNodes = () => {
        if (selectedNodes.length === validNodes.length) {
            setSelectedNodes([]);
        } else {
            setSelectedNodes(validNodes.map(node => node.name));
        }
    };

    const buildStressTestCommand = () => {
        const testConfig = getSelectedTestConfig();
        if (!testConfig) return '';

        let command = '';
        
        switch (selectedTest) {
            case 'gpu_stress':
                command = `# GPU Stress Test Configuration
duration=${testDuration}
intensity=${testIntensity}
memory_test=${gpuMemoryTest}
compute_test=${gpuComputeTest}
nodes="${selectedNodes.join(',')}"

# This will execute:
# - GPU-burn or similar CUDA stress test
# - Memory bandwidth testing with different patterns
# - Thermal monitoring during stress test
# - Performance metrics collection`;
                break;
                
            case 'iops_stress':
                command = `# IOPS Stress Test Configuration
duration=${testDuration}
intensity=${testIntensity}
block_size=${ioBlockSize}
pattern=${ioPattern}
read_write_ratio=${ioReadWriteRatio}:${100-ioReadWriteRatio}
nodes="${selectedNodes.join(',')}"

# This will execute:
# - fio (Flexible I/O tester) with specified parameters
# - Multiple I/O patterns and block sizes
# - Read/write ratio testing
# - IOPS and bandwidth measurement`;
                break;
                
            case 'memory_bandwidth':
                command = `# Memory Bandwidth Test Configuration
duration=${testDuration}
intensity=${testIntensity}
nodes="${selectedNodes.join(',')}"

# This will execute:
# - STREAM benchmark for memory bandwidth
# - Memory latency testing with different access patterns
# - Cache performance analysis`;
                break;
                
            case 'network_bandwidth':
                command = `# Network Bandwidth Test Configuration
duration=${testDuration}
intensity=${testIntensity}
nodes="${selectedNodes.join(',')}"

# This will execute:
# - iperf3 bandwidth testing between nodes
# - Latency measurement with ping/netperf
# - InfiniBand performance testing (if available)`;
                break;
        }
        
        return command;
    };

    const executeStressTest = async () => {
        if (!selectedTest || selectedNodes.length === 0) {
            return;
        }

        setIsRunning(true);
        
        try {
            // Build test configuration
            const testConfig = {
                testType: selectedTest,
                nodes: selectedNodes,
                duration: testDuration,
                intensity: testIntensity,
                options: {}
            };

            // Add test-specific options
            if (selectedTest === 'gpu_stress') {
                testConfig.options = {
                    memoryTest: gpuMemoryTest,
                    computeTest: gpuComputeTest
                };
            } else if (selectedTest === 'iops_stress') {
                testConfig.options = {
                    blockSize: ioBlockSize,
                    pattern: ioPattern,
                    readWriteRatio: ioReadWriteRatio
                };
            }

            // Execute the stress test via Redux action
            const result = await dispatch(Actions.executeStressTest(clusterId, testConfig));
            
            // Show results
            setTestResults({
                testType: selectedTest,
                nodes: selectedNodes,
                duration: testDuration,
                startTime: new Date().toISOString(),
                status: result.status,
                results: result.status === 'success' ? {
                    message: result.message,
                    successfulNodes: result.results?.filter(r => r.status === 'started').length || 0,
                    failedNodes: result.results?.filter(r => r.status === 'failed').length || 0
                } : {
                    error: result.message
                }
            });
            
            setShowResultsDialog(true);
            
        } catch (error) {
            console.error('Stress test execution failed:', error);
            
            // Show error results
            setTestResults({
                testType: selectedTest,
                nodes: selectedNodes,
                duration: testDuration,
                startTime: new Date().toISOString(),
                status: 'error',
                results: {
                    error: error.message || 'Failed to execute stress test'
                }
            });
            
            setShowResultsDialog(true);
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <div className={className}>
            <Grid container spacing={3}>
                {/* Test Type Selection */}
                <Grid item xs={12}>
                    <Paper className="p-20">
                        <Grid container spacing={2}>
                            {stressTestTypes.map(test => {
                                const isComingSoon = test.id === 'network_bandwidth';
                                return (
                                    <Grid item xs={12} sm={6} md={3} key={test.id}>
                                        <Card 
                                            variant="outlined"
                                            className={`transition-all ${selectedTest === test.id ? 'border-blue bg-blue-50' : ''}`}
                                            onClick={() => {
                                                if (isComingSoon) return;
                                                if (test.id === 'iops_stress') {
                                                    setShowIOPSDialog(true);
                                                } else if (test.id === 'memory_bandwidth') {
                                                    setShowMemoryBandwidthDialog(true);
                                                } else if (test.id === 'gpu_stress') {
                                                    setShowGPUDialog(true);
                                                } else {
                                                    setSelectedTest(test.id);
                                                }
                                            }}
                                            style={{
                                                cursor: isComingSoon ? 'not-allowed' : 'pointer',
                                                opacity: isComingSoon ? 0.6 : 1
                                            }}
                                            aria-disabled={isComingSoon}
                                        >
                                            <CardContent className="p-16 text-center" style={{ position: 'relative' }}>
                                                {isComingSoon && (
                                                    <Chip 
                                                        label="Soon" 
                                                        size="small" 
                                                        style={{ position: 'absolute', top: 8, right: 8 }} 
                                                    />
                                                )}
                                                <Icon 
                                                    style={{ color: test.color, fontSize: 32 }} 
                                                    className="mb-8"
                                                >
                                                    {test.icon}
                                                </Icon>
                                                <Typography variant="subtitle2" className="font-bold mb-4">
                                                    {test.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" className="text-12">
                                                    {test.description}
                                                </Typography>
                                                <Typography variant="caption" className="block mt-8 text-blue">
                                                    {isComingSoon ? 'Coming soon' : `Est. ${test.estimatedTime}`}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Test Configuration */}
                {selectedTest && (
                    <Grid item xs={12} md={8}>
                        <Paper className="p-20">
                            <Typography variant="h6" className="mb-16">
                                Test Parameters
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Test Duration (seconds)</InputLabel>
                                        <Select
                                            value={testDuration}
                                            onChange={(e) => setTestDuration(e.target.value)}
                                            label="Test Duration (seconds)"
                                        >
                                            {getSelectedTestConfig()?.duration.map(duration => (
                                                <MenuItem key={duration} value={duration}>
                                                    {duration} seconds
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Test Intensity</InputLabel>
                                        <Select
                                            value={testIntensity}
                                            onChange={(e) => setTestIntensity(e.target.value)}
                                            label="Test Intensity"
                                        >
                                            {intensityLevels.map(level => (
                                                <MenuItem key={level.value} value={level.value}>
                                                    {level.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* GPU-specific options */}
                                {selectedTest === 'gpu_stress' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={gpuMemoryTest}
                                                        onChange={(e) => setGpuMemoryTest(e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Memory Bandwidth Test"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={gpuComputeTest}
                                                        onChange={(e) => setGpuComputeTest(e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Compute Performance Test"
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* IOPS-specific options */}
                                {selectedTest === 'iops_stress' && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Block Size</InputLabel>
                                                <Select
                                                    value={ioBlockSize}
                                                    onChange={(e) => setIoBlockSize(e.target.value)}
                                                    label="Block Size"
                                                >
                                                    {ioBlockSizes.map(size => (
                                                        <MenuItem key={size} value={size}>{size}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>I/O Pattern</InputLabel>
                                                <Select
                                                    value={ioPattern}
                                                    onChange={(e) => setIoPattern(e.target.value)}
                                                    label="I/O Pattern"
                                                >
                                                    {ioPatterns.map(pattern => (
                                                        <MenuItem key={pattern.value} value={pattern.value}>
                                                            {pattern.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Read/Write Ratio (%)"
                                                type="number"
                                                value={ioReadWriteRatio}
                                                onChange={(e) => setIoReadWriteRatio(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                                inputProps={{ min: 0, max: 100 }}
                                                helperText={`${ioReadWriteRatio}% read, ${100-ioReadWriteRatio}% write`}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>
                )}

                {/* Node Selection */}
                {selectedTest && (
                    <Grid item xs={12} md={4}>
                        <Paper className="p-20">
                            <div className="flex items-center justify-between mb-16">
                                <Typography variant="h6">
                                    Target Nodes
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={handleSelectAllNodes}
                                    color="primary"
                                >
                                    {selectedNodes.length === validNodes.length ? 'Deselect All' : 'Select All'}
                                </Button>
                            </div>
                            
                            <Typography variant="body2" color="textSecondary" className="mb-12">
                                {getNodeSelectionText()}
                            </Typography>
                            
                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                {validNodes.map(node => (
                                    <FormControlLabel
                                        key={node.name}
                                        control={
                                            <Switch
                                                checked={selectedNodes.includes(node.name)}
                                                onChange={() => handleNodeToggle(node.name)}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={
                                            <div>
                                                <Typography variant="body2">{node.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {node.nodeType || 'Compute Node'}
                                                </Typography>
                                            </div>
                                        }
                                        className="block mb-4"
                                    />
                                ))}
                            </div>
                        </Paper>
                    </Grid>
                )}

                {/* Execute Test */}
                {selectedTest && selectedNodes.length > 0 && (
                    <Grid item xs={12}>
                        <Paper className="p-20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Typography variant="h6" className="mb-8">
                                        Ready to Execute
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {getSelectedTestConfig()?.name} on {selectedNodes.length} node(s) for {testDuration} seconds
                                    </Typography>
                                </div>
                                
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={executeStressTest}
                                    disabled={isRunning}
                                    startIcon={isRunning ? <LinearProgress className="w-20" /> : <Icon>play_arrow</Icon>}
                                >
                                    {isRunning ? 'Running Test...' : 'Start Stress Test'}
                                </Button>
                            </div>
                            
                            {isRunning && (
                                <div className="mt-16">
                                    <LinearProgress />
                                    <Typography variant="caption" color="textSecondary" className="mt-8 block">
                                        Test in progress... This may take several minutes.
                                    </Typography>
                                </div>
                            )}
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Results Dialog */}
            <Dialog
                open={showResultsDialog}
                onClose={() => setShowResultsDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Stress Test Results
                </DialogTitle>
                <DialogContent>
                    {testResults && (
                        <div>
                            <Typography variant="h6" className="mb-16">
                                {getSelectedTestConfig()?.name} - Completed
                            </Typography>
                            
                            <Grid container spacing={2}>
                                {selectedTest === 'iops_stress' && testResults.results && !testResults.results.error && (
                                    <>
                                        <Grid item xs={12}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-blue mb-4">
                                                    Test Executed Successfully
                                                </Typography>
                                                <Typography variant="body2">
                                                    Successful nodes: {testResults.results.successfulNodes || 0}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Failed nodes: {testResults.results.failedNodes || 0}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" className="mt-8 block">
                                                    {testResults.results.message}
                                                </Typography>
                                            </Card>
                                        </Grid>
                                    </>
                                )}

                                {testResults.results?.error && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined" className="text-center p-12 bg-red-50">
                                            <Typography variant="h6" className="text-red mb-4">
                                                Test Failed
                                            </Typography>
                                            <Typography variant="body2" color="error">
                                                {testResults.results.error}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                )}

                                {selectedTest === 'gpu_stress' && testResults.results && !testResults.results.error && (
                                    <>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-orange">
                                                    {testResults.results.averageGpuUtilization}%
                                                </Typography>
                                                <Typography variant="caption">Avg GPU Util</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-red">
                                                    {testResults.results.peakTemperature}°C
                                                </Typography>
                                                <Typography variant="caption">Peak Temp</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-blue">
                                                    {testResults.results.memoryBandwidth} GB/s
                                                </Typography>
                                                <Typography variant="caption">Memory BW</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-green">
                                                    {testResults.results.computePerformance} TFLOPS
                                                </Typography>
                                                <Typography variant="caption">Compute</Typography>
                                            </Card>
                                        </Grid>
                                    </>
                                )}

                                {selectedTest === 'iops_stress' && testResults.results && testResults.results.readIOPS && (
                                    <>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-orange">
                                                    {testResults.results.averageGpuUtilization}%
                                                </Typography>
                                                <Typography variant="caption">Avg GPU Util</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-red">
                                                    {testResults.results.peakTemperature}°C
                                                </Typography>
                                                <Typography variant="caption">Peak Temp</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-blue">
                                                    {testResults.results.memoryBandwidth} GB/s
                                                </Typography>
                                                <Typography variant="caption">Memory BW</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-green">
                                                    {testResults.results.computePerformance} TFLOPS
                                                </Typography>
                                                <Typography variant="caption">Compute</Typography>
                                            </Card>
                                        </Grid>
                                    </>
                                )}
                                
                                {selectedTest === 'iops_stress' && testResults.results && (
                                    <>
                                        <Grid item xs={6} sm={2}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-blue">
                                                    {testResults.results.readIOPS.toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Read IOPS</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-green">
                                                    {testResults.results.writeIOPS.toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Write IOPS</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-blue">
                                                    {testResults.results.readBandwidth} MB/s
                                                </Typography>
                                                <Typography variant="caption">Read BW</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-green">
                                                    {testResults.results.writeBandwidth} MB/s
                                                </Typography>
                                                <Typography variant="caption">Write BW</Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Card variant="outlined" className="text-center p-12">
                                                <Typography variant="h6" className="text-orange">
                                                    {testResults.results.averageLatency} ms
                                                </Typography>
                                                <Typography variant="caption">Avg Latency</Typography>
                                            </Card>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            
                            <div className="mt-24">
                                <Typography variant="subtitle2" className="mb-8">Test Summary</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    • Test Duration: {testResults.duration} seconds<br/>
                                    • Nodes Tested: {testResults.nodes.join(', ')}<br/>
                                    • Start Time: {moment(testResults.startTime).format('YYYY-MM-DD HH:mm:ss')}<br/>
                                    • Status: {testResults.status}
                                </Typography>
                            </div>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResultsDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* IOPS Stress Test Dialog */}
            <IOPSStressDialog 
                open={showIOPSDialog}
                onClose={() => setShowIOPSDialog(false)}
                clusterId={clusterId}
            />

            {/* Memory Bandwidth Test Dialog */}
            <MemoryBandwidthDialog 
                open={showMemoryBandwidthDialog}
                onClose={() => setShowMemoryBandwidthDialog(false)}
                clusterId={clusterId}
                nodes={validNodes}
            />

            {/* GPU Stress Test Dialog */}
            <GPUStressDialog 
                open={showGPUDialog}
                onClose={() => setShowGPUDialog(false)}
                clusterId={clusterId}
            />
        </div>
    );
};

export default StressTestPanel;
